import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PlanningSprint } from '../../services/strategicPlanningApi';
import { COMPANY_AREAS, CompanyAreaId, formatCompanyArea } from '../../types/CompanyArea';
import {
  getSprintTimeframe,
  PLANNING_BACKLOG_ID,
  sortSprintsFromCurrentToLast,
  SprintTimeframe,
} from '../../types/PlanningSprint';
import { formatDateBr } from '../../utils/formatDateBr';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { EditIconButton } from '../ui/EditIconButton';

type PlanningSprintPickerProps = {
  area: CompanyAreaId;
  sprints: PlanningSprint[];
  selectedSprintId: string;
  canWrite: boolean;
  onAreaChange: (area: CompanyAreaId) => void;
  onSprintChange: (sprintId: string) => void;
  onCreateSprint: () => void;
  onEditSprint: (sprint: PlanningSprint) => void;
  onDeleteSprint: (sprint: PlanningSprint) => void;
};

const TIMEFRAME_LABEL: Record<SprintTimeframe, string> = {
  past: 'Anterior',
  current: 'Atual',
  future: 'Futura',
};

const TIMEFRAME_CLASS: Record<SprintTimeframe, string> = {
  past: 'bg-secondary-100 text-secondary-700',
  current: 'bg-primary-100 text-primary-700',
  future: 'bg-sky-100 text-sky-700',
};

export function PlanningSprintPicker({
  area,
  sprints,
  selectedSprintId,
  canWrite,
  onAreaChange,
  onSprintChange,
  onCreateSprint,
  onEditSprint,
  onDeleteSprint,
}: PlanningSprintPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedSprint = sprints.find((sprint) => sprint.id === selectedSprintId) ?? null;

  const filteredSprints = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const items = normalized
      ? sprints.filter((sprint) => sprint.title.toLowerCase().includes(normalized))
      : sprints;

    return sortSprintsFromCurrentToLast(items);
  }, [query, sprints]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    setQuery('');
  }, [open, area]);

  const selectedLabel = selectedSprint
    ? selectedSprint.title
    : selectedSprintId === PLANNING_BACKLOG_ID
      ? 'Backlog'
      : 'Selecionar sprint';
  const selectedTimeframe = selectedSprint ? getSprintTimeframe(selectedSprint) : null;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <label className="block min-w-[200px] max-w-xs">
        <span className="erp-label">Área</span>
        <select
          className="erp-input"
          value={area}
          onChange={(event) => onAreaChange(event.target.value as CompanyAreaId)}
        >
          {COMPANY_AREAS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <div ref={rootRef} className="relative min-w-[260px] max-w-sm flex-1">
        <span className="erp-label">Sprint</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="erp-input flex w-full items-center justify-between gap-2 text-left"
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">{selectedLabel}</span>
              {selectedTimeframe ? (
                <TimeframeBadge timeframe={selectedTimeframe} />
              ) : selectedSprintId === PLANNING_BACKLOG_ID ? (
                <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700">
                  Sem sprint
                </span>
              ) : null}
            </span>
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-secondary-500" />
          </button>
          {canWrite && selectedSprint ? (
            <>
              <EditIconButton label="Editar sprint" onClick={() => onEditSprint(selectedSprint)} />
              <DeleteIconButton label="Excluir sprint" onClick={() => onDeleteSprint(selectedSprint)} />
            </>
          ) : null}
        </div>

        {open ? (
          <div className="absolute z-30 mt-1 w-full min-w-[280px] overflow-hidden rounded-lg border border-secondary-200 bg-white shadow-xl">
            <div className="border-b border-secondary-100 p-2">
              <label className="relative block">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
                <input
                  className="erp-input pl-8"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Filtrar sprints"
                  autoFocus
                />
              </label>
            </div>
            <div className="max-h-72 overflow-y-auto py-1" role="listbox">
              <SprintOption
                selected={selectedSprintId === PLANNING_BACKLOG_ID}
                title="Backlog"
                subtitle="Tarefas ainda sem sprint"
                onClick={() => {
                  onSprintChange(PLANNING_BACKLOG_ID);
                  setOpen(false);
                }}
              />
              {filteredSprints.map((sprint) => {
                const timeframe = getSprintTimeframe(sprint);
                return (
                  <SprintOption
                    key={sprint.id}
                    selected={sprint.id === selectedSprintId}
                    title={sprint.title}
                    subtitle={`${formatDateBr(sprint.startDate)} – ${formatDateBr(sprint.endDate)} · ${formatCompanyArea(sprint.area)}`}
                    timeframe={timeframe}
                    onClick={() => {
                      onSprintChange(sprint.id);
                      setOpen(false);
                    }}
                  />
                );
              })}
              {filteredSprints.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-secondary-500">
                  Nenhuma sprint nesta área.
                </p>
              ) : null}
            </div>
            {canWrite ? (
              <div className="border-t border-secondary-100 p-2">
                <button
                  type="button"
                  className="w-full rounded-md px-3 py-2 text-left text-sm font-medium text-primary-700 hover:bg-primary-50"
                  onClick={() => {
                    setOpen(false);
                    onCreateSprint();
                  }}
                >
                  + Nova sprint
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

type SprintOptionProps = {
  selected: boolean;
  title: string;
  subtitle: string;
  timeframe?: SprintTimeframe;
  onClick: () => void;
};

function SprintOption({ selected, title, subtitle, timeframe, onClick }: SprintOptionProps) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-primary-50 ${
        selected ? 'bg-primary-50' : ''
      }`}
      onClick={onClick}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-secondary-900">{title}</span>
        <span className="block truncate text-xs text-secondary-500">{subtitle}</span>
      </span>
      {timeframe ? <TimeframeBadge timeframe={timeframe} /> : null}
    </button>
  );
}

function TimeframeBadge({ timeframe }: { timeframe: SprintTimeframe }) {
  return (
    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${TIMEFRAME_CLASS[timeframe]}`}>
      {TIMEFRAME_LABEL[timeframe]}
    </span>
  );
}
